import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * Fullscreen media lightbox — Airbnb/Vercel-style.
 * Controls: ← / → to switch, Esc to close, click backdrop to close.
 */
export default function MediaLightbox({ items, index, onClose, onIndexChange }) {
  const open = index != null && index >= 0 && index < (items?.length || 0);

  const next = useCallback(() => {
    if (!open) return;
    onIndexChange((index + 1) % items.length);
  }, [open, index, items, onIndexChange]);

  const prev = useCallback(() => {
    if (!open) return;
    onIndexChange((index - 1 + items.length) % items.length);
  }, [open, index, items, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    // Lock page scroll while the lightbox is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, next, prev, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="media-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="media-lightbox-close"
            className="absolute top-5 right-5 z-10 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
            aria-label="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          {items.length > 1 && (
            <div className="absolute top-5 left-5 text-white/80 text-[13px] font-medium tracking-wide">
              {index + 1} / {items.length}
            </div>
          )}

          {/* Prev / Next */}
          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                data-testid="media-lightbox-prev"
                className="absolute left-3 md:left-6 z-10 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
                aria-label="Previous (Left arrow)"
              ><ChevronLeft className="w-6 h-6" /></button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                data-testid="media-lightbox-next"
                className="absolute right-3 md:right-6 z-10 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
                aria-label="Next (Right arrow)"
              ><ChevronRight className="w-6 h-6" /></button>
            </>
          )}

          {/* Media panel — click-through to backdrop is blocked here so the
              user doesn't accidentally close by clicking the image. */}
          <motion.div
            key={items[index].id}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-[92vw] max-h-[86vh] w-full h-full flex flex-col items-center justify-center"
          >
            {items[index].type === "video" ? (
              <video
                src={items[index].url}
                controls
                autoPlay
                className="max-w-full max-h-full object-contain rounded-lg bg-black"
              />
            ) : (
              <img
                src={items[index].url}
                alt={items[index].caption || "Coach media"}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            )}
            {items[index].caption ? (
              <p className="mt-4 text-white/85 text-[14px] leading-relaxed max-w-[68ch] text-center px-4">
                {items[index].caption}
              </p>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
